import React from 'react';
import { Box, Typography, IconButton, CircularProgress, Checkbox, Tooltip, TextField, InputAdornment } from '@mui/material';
import { Search, X } from 'lucide-react';
import { useSettings } from '../../../providers/settings-provider';
import { highlightMatch } from '../../ui/highlight-match';

export interface IncludedTransactionRow {
    guid: string;
    date: string;
    description: string;
    amount: number;
    bankAccountName: string | null;
}

interface Props {
    transactions: IncludedTransactionRow[];
    // Transactions marked as outliers - listed greyed out, left out of the amount.
    excludedGuids: Set<string>;
    onToggleExcluded: (guid: string) => void;
    isLoading: boolean;
    emptyMessage: string;
    onClose: () => void;
}

type SortField = 'date' | 'amount' | 'outlier';

// The direction a column starts in when first picked - newest, largest,
// outliers first.
const DEFAULT_DIRECTION: Record<SortField, 'asc' | 'desc'> = { date: 'desc', amount: 'desc', outlier: 'desc' };


// Side panel of the General Provision edit modal, listing the transactions
// its amount is built from - the same sidebar as Edit Provision's
// Occurrences panel (provision-occurrences-panel.tsx): fixed title bar,
// search and column header, only the row list scrolls. The Outlier column
// takes a transaction out of the amount without hiding it.
const GeneralProvisionIncludedPanel: React.FC<Props> = ({ transactions, excludedGuids, onToggleExcluded, isLoading, emptyMessage, onClose }) => {
    const { formatDate, formatAmount } = useSettings();
    const [sort, setSort] = React.useState<{ field: SortField; direction: 'asc' | 'desc' }>({ field: 'date', direction: 'desc' });
    const [search, setSearch] = React.useState('');
    const term = search.trim();

    const toggleSort = (field: SortField) => setSort(prev => (prev.field === field
        ? { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { field, direction: DEFAULT_DIRECTION[field] }));

    // What each row displays, so search matches exactly what's on screen.
    const rows = React.useMemo(() => transactions.map(row => ({
        ...row,
        dateText: formatDate(new Date(row.date)),
        amountText: formatAmount ? formatAmount(row.amount) : String(row.amount),
    })), [transactions, formatDate, formatAmount]);

    const visible = React.useMemo(() => {
        const needle = term.toLowerCase();
        const matched = needle
            // Date, amount and description - as displayed, plus the plain amount.
            ? rows.filter(r => [r.dateText, r.amountText, String(r.amount), r.description].some(t => (t || '').toLowerCase().includes(needle)))
            : rows;
        const dir = sort.direction === 'asc' ? 1 : -1;
        const byDate = (a: typeof rows[number], b: typeof rows[number]) => new Date(b.date).getTime() - new Date(a.date).getTime();
        return [...matched].sort((a, b) => {
            let diff = 0;
            if (sort.field === 'date') diff = new Date(a.date).getTime() - new Date(b.date).getTime();
            else if (sort.field === 'amount') diff = Number(a.amount) - Number(b.amount);
            else diff = Number(excludedGuids.has(a.guid)) - Number(excludedGuids.has(b.guid));
            return diff * dir || byDate(a, b);
        });
    }, [rows, term, sort, excludedGuids]);

    const header = (field: SortField, label: string, sx: object) => (
        <Box onClick={() => toggleSort(field)} sx={{ display: 'flex', alignItems: 'center', gap: 0.25, cursor: 'pointer', userSelect: 'none', ...sx }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-muted)' }}>{label}</Typography>
            {/* Every column shows it's sortable - muted until it's the active one. */}
            <Typography sx={{ fontSize: 10, color: sort.field === field ? 'var(--color-primary)' : 'var(--color-text-muted)', opacity: sort.field === field ? 1 : 0.5 }}>
                {sort.field === field ? (sort.direction === 'asc' ? '▲' : '▼') : (DEFAULT_DIRECTION[field] === 'asc' ? '▲' : '▼')}
            </Typography>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', minHeight: 0 }}>
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1,
                flexShrink: 0, background: 'var(--color-background, #fff)',
            }}>
                <Typography sx={{ fontWeight: 700, fontSize: 13 }}>Included Transactions</Typography>
                <IconButton size="small" onClick={onClose} aria-label="Hide included transactions">
                    <X size={16} />
                </IconButton>
            </Box>
            {isLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress size={24} />
                </Box>
            ) : rows.length === 0 ? (
                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', textAlign: 'center', py: 4, px: 1.5 }}>
                    {emptyMessage}
                </Typography>
            ) : (
                <>
                    <Box sx={{ px: 1.5, pb: 1, flexShrink: 0 }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search transactions..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            InputProps={{
                                sx: { fontSize: 12, height: 32 },
                                startAdornment: <InputAdornment position="start"><Search size={14} color="var(--color-primary)" /></InputAdornment>,
                                endAdornment: search ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" edge="end" onClick={() => setSearch('')} aria-label="Clear search">
                                            <X size={12} />
                                        </IconButton>
                                    </InputAdornment>
                                ) : undefined,
                            }}
                        />
                    </Box>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.5,
                        py: 0.75,
                        borderTop: '1px solid #e0e0e0',
                        borderBottom: '1px solid #e0e0e0',
                        background: 'var(--color-container-background)',
                        flexShrink: 0,
                    }}>
                        {header('date', 'Date', { flex: '1 1 0' })}
                        {header('amount', 'Amount', { flex: '1 1 0', justifyContent: 'flex-end' })}
                        <Tooltip title="Outliers are left out of the amount">
                            <Box sx={{ flex: '0 0 48px', display: 'flex', justifyContent: 'center' }}>
                                {header('outlier', 'Outlier', {})}
                            </Box>
                        </Tooltip>
                    </Box>
                    <Box sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': { width: 5 },
                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                        '&::-webkit-scrollbar-thumb': { background: 'rgba(0, 0, 0, 0.2)', borderRadius: 3 },
                        scrollbarWidth: 'thin',
                    }}>
                        {visible.length === 0 && (
                            <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', textAlign: 'center', py: 3, px: 1.5 }}>
                                No transactions match "{term}".
                            </Typography>
                        )}
                        {visible.map(row => {
                            const excluded = excludedGuids.has(row.guid);
                            return (
                                <Box key={row.guid} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.65, borderBottom: '1px solid #f0f0f0' }}>
                                    {/* Greyed out (like a deleted occurrence) when it's an outlier. */}
                                    <Box sx={{ flex: '2 1 0', minWidth: 0, opacity: excluded ? 0.45 : 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography sx={{ flex: '1 1 0', fontSize: 12 }}>{highlightMatch(row.dateText, term)}</Typography>
                                            <Typography sx={{
                                                flex: '1 1 0', fontSize: 12, textAlign: 'right',
                                                color: Number(row.amount) <= 0 ? 'var(--color-negative-amount)' : 'var(--color-default-text)',
                                                textDecoration: excluded ? 'line-through' : 'none',
                                            }}>
                                                {highlightMatch(row.amountText, term)}
                                            </Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                            {highlightMatch(row.description, term)}{row.bankAccountName ? ` · ${row.bankAccountName}` : ''}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ flex: '0 0 48px', display: 'flex', justifyContent: 'center' }}>
                                        <Checkbox
                                            size="small"
                                            checked={excluded}
                                            onChange={() => onToggleExcluded(row.guid)}
                                            inputProps={{ 'aria-label': `Mark ${row.description} as an outlier` }}
                                            sx={{ p: 0.5, color: 'var(--color-text-muted)', '&.Mui-checked': { color: 'var(--color-primary)' } }}
                                        />
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </>
            )}
        </Box>
    );
};

export default GeneralProvisionIncludedPanel;
